"use client";

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/auth";

interface WrappedUploadResponse {
  data: { url: string };
}

interface FlatUploadResponse {
  url: string;
}

function isWrappedUploadResponse(
  value: unknown,
): value is WrappedUploadResponse {
  if (value === null || typeof value !== "object") return false;
  const data = Reflect.get(value, "data");
  if (data === null || typeof data !== "object") return false;
  return typeof Reflect.get(data, "url") === "string";
}

function isFlatUploadResponse(value: unknown): value is FlatUploadResponse {
  if (value === null || typeof value !== "object") return false;
  return typeof Reflect.get(value, "url") === "string";
}

interface UseUploadElectionAttachmentReturn {
  uploadAsync: (
    file: File,
    onProgress?: (pct: number) => void,
  ) => Promise<string>;
  isPending: boolean;
}

/**
 * XHR-based upload hook for POST /election/attachment (multipart).
 *
 * Uses raw XMLHttpRequest (not openapi-fetch) for xhr.upload.onprogress.
 * fetch streams do not expose upload progress reliably across all targets.
 *
 * r4 §5.2:
 * - Token read at XHR send time via useAuth.getState().token (not subscribed)
 * - xhrRef tracks in-flight request; useEffect cleanup aborts on unmount
 * - Progress capped at 99 until onload confirms 201, then resolves 100
 */
export function useUploadElectionAttachment(): UseUploadElectionAttachmentReturn {
  const [isPending, setIsPending] = useState(false);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  useEffect(() => {
    return () => {
      xhrRef.current?.abort();
      xhrRef.current = null;
    };
  }, []);

  function uploadAsync(
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<string> {
    setIsPending(true);

    return new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      const baseUrl =
        process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:7000";
      xhr.open("POST", `${baseUrl}/election/attachment`, true);

      const token = useAuth.getState().token;
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      xhr.responseType = "json";

      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && onProgress) {
          const pct = Math.round((ev.loaded / ev.total) * 100);
          onProgress(Math.min(99, pct));
        }
      };

      xhr.onload = () => {
        setIsPending(false);
        if (xhr.status === 201) {
          const body: unknown = xhr.response;
          let url: string | undefined;

          if (isWrappedUploadResponse(body)) {
            url = body.data.url;
          } else if (isFlatUploadResponse(body)) {
            url = body.url;
          }

          if (typeof url !== "string") {
            reject(new Error("invalid_response_shape"));
            return;
          }

          onProgress?.(100);
          resolve(url);
        } else {
          reject(
            Object.assign(new Error(`upload_failed_status_${xhr.status}`), {
              status: xhr.status,
              response: xhr.response,
            }),
          );
        }

        if (xhrRef.current === xhr) {
          xhrRef.current = null;
        }
      };

      xhr.onerror = () => {
        setIsPending(false);
        if (xhrRef.current === xhr) xhrRef.current = null;
        reject(new Error("network_error"));
      };

      xhr.onabort = () => {
        setIsPending(false);
        if (xhrRef.current === xhr) xhrRef.current = null;
        reject(new Error("aborted"));
      };

      const fd = new FormData();
      fd.append("file", file);
      xhr.send(fd);
    });
  }

  return { uploadAsync, isPending };
}
